/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  setRegions,
  setCatalog,
  setGeojson,
  setAlertsGeojson,
  setCatalogLoading,
  setGeojsonLoading,
  setError,
  setCompareCatalog,
  setCompareGeojson,
  setCompareAlertsGeojson,
  setCommunesIndex,
} from "../../redux/reducers/dataSlice";
import { geojson as fgbGeojson } from "flatgeobuf";

import { dataLoaderSettings } from "../../settings/settings";

const COMMUNES_INDEX_URL =
  "https://d2lwejudaxss9k.cloudfront.net/DatosProcesados/WazeChile/communes.json";

/** Enumera "YYYY-MM-DD" inclusivos. */
function enumerateDays(start, end) {
  const out = [];
  const s = new Date(`${start}T12:00:00`);
  const e = new Date(`${end}T12:00:00`);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    out.push(iso);
  }
  return out;
}

/** Devuelve el conjunto de URLs a fetch-ear para un rango.
 *  Estrategia:
 *    - Sin filtro horario  → daily de cada día.
 *    - Con filtro horario  → hours [HH_start..HH_end] de CADA día del
 *      rango (evita bajar el daily completo cuando sólo querés una
 *      ventana). El filtro fino por minuto lo aplica el consumidor
 *      client-side sobre props.date/time. */
// Descarga y parsea un archivo de features de la región. Agnóstico al
// formato por extensión: `.fgb` → FlatGeobuf (nuevo, gzipeado; el browser
// descomprime y flatgeobuf parsea el ArrayBuffer); cualquier otra cosa
// (`.geojson.gz` histórico pre-migración) → GeoJSON. Devuelve [] ante error.
async function fetchFeatures(url, signal) {
  try {
    const resp = await fetch(url, { signal });
    if (!resp.ok) return [];
    if (url.endsWith(".fgb")) {
      // flatgeobuf 4.x: deserialize(bytes) devuelve un async-iterable de
      // features (no un FeatureCollection). El browser ya descomprimió el
      // gzip, así que arrayBuffer() son bytes FGB crudos.
      const buf = await resp.arrayBuffer();
      const feats = [];
      for await (const f of fgbGeojson.deserialize(new Uint8Array(buf))) {
        feats.push(f);
      }
      return feats;
    }
    const gj = await resp.json();
    return gj?.features || [];
  } catch (e) {
    return [];
  }
}

async function fetchRangeFeatures({
  apiBase, region, layer, dateStart, dateEnd, timeStart, timeEnd, signal,
}) {
  const hasTimeFilter = Boolean(timeStart && timeEnd);
  const days = enumerateDays(dateStart, dateEnd);

  // Catálogo de cada día del rango (en paralelo). El primero lo
  // devolvemos como `_mainCatalog` para alimentar KPIs/labels.
  const catalogs = await Promise.all(
    days.map((d) =>
      fetch(`${apiBase}/regions/${region}/days/${d}`, { signal })
        .then((r) => r.ok ? r.json() : null)
        .catch(() => null),
    ),
  );
  const mainCatalog = catalogs[0];

  // Un solo archivo (FGB) por región. La comuna se filtra client-side en el
  // consumidor (por `properties.city`), así que acá siempre traemos la región.
  const pickFromCatalog = (cat) => {
    if (!cat) return null;
    return {
      daily: layer === "alerts" ? cat.alerts_daily : cat.daily,
      hours: layer === "alerts" ? cat.alerts_hours : cat.hours,
    };
  };

  const urls = [];
  if (hasTimeFilter) {
    const startH = parseInt(timeStart.slice(0, 2), 10);
    const endH   = parseInt(timeEnd.slice(0, 2), 10);
    for (const cat of catalogs) {
      const src = pickFromCatalog(cat);
      if (!src) continue;
      for (let h = startH; h <= endH; h++) {
        const hh = String(h).padStart(2, "0");
        const t = src.hours?.[hh];
        if (t?.url) urls.push(t.url);
      }
    }
  } else {
    for (const cat of catalogs) {
      const src = pickFromCatalog(cat);
      if (!src) continue;
      if (src.daily?.url) {
        // Día consolidado: un solo fetch del diario.
        urls.push(src.daily.url);
      } else if (src.hours) {
        // HOY: el diario por comuna se consolida 1×/día (00:05 del día
        // siguiente), así que para el día en curso todavía no existe.
        // Armamos la vista concatenando los horarios (frescos cada 2 min).
        for (const hh of Object.keys(src.hours).sort()) {
          const t = src.hours[hh];
          if (t?.url) urls.push(t.url);
        }
      }
    }
  }

  if (urls.length === 0) return { type: "FeatureCollection", features: [], _mainCatalog: mainCatalog };

  const perUrl = await Promise.all(urls.map((u) => fetchFeatures(u, signal)));
  // Concat manual sin spread: con datasets grandes (alerts multi-día) un
  // `all.push(...features)` con >~64k elementos tira "Maximum call stack
  // size exceeded" porque el spread pasa cada item como argumento.
  let all = [];
  for (const feats of perUrl) {
    if (feats?.length) all = all.concat(feats);
  }
  return { type: "FeatureCollection", features: all, _mainCatalog: mainCatalog };
}


const DataLoader = () => {
  const dispatch = useDispatch();
  const apiBase = dataLoaderSettings.apiURL?.replace(/\/$/, "");

  const regions = useSelector((s) => s.dataState.regions);
  const selectedRegion = useSelector((s) => s.dataState.selectedRegion);
  const dateStart = useSelector((s) => s.dataState.dateStart);
  const dateEnd   = useSelector((s) => s.dataState.dateEnd);
  const timeStart = useSelector((s) => s.dataState.timeStart);
  const timeEnd   = useSelector((s) => s.dataState.timeEnd);
  const showJams = useSelector((s) => s.dataState.showJams);
  const showAlerts = useSelector((s) => s.dataState.showAlerts);
  const selectedCommune = useSelector((s) => s.dataState.selectedCommune);
  const compareEnabled = useSelector((s) => s.dataState.compareEnabled);
  const compareDateStart = useSelector((s) => s.dataState.compareDateStart);
  const compareDateEnd = useSelector((s) => s.dataState.compareDateEnd);
  const compareTimeStart = useSelector((s) => s.dataState.compareTimeStart);
  const compareTimeEnd = useSelector((s) => s.dataState.compareTimeEnd);

  // Acumuladores por comuna dentro de un mismo CONTEXTO (región+fechas+horas).
  // Al navegar entre comunas de la misma región los datos ya cargados se
  // mantienen y se van sumando, en vez de reemplazarse (la partición por
  // comuna sigue acotando cada fetch). Al cambiar el contexto se descartan.
  // En modo comparar mostramos una sola comuna a la vez.
  const jamsAccum = useRef({ key: null, byCommune: new Map() });
  const alertsAccum = useRef({ key: null, byCommune: new Map() });
  const accumKey = `${selectedRegion}|${dateStart}|${dateEnd}|${timeStart || ""}|${timeEnd || ""}`;

  // 1) regiones (boot)
  useEffect(() => {
    if (regions.length > 0) return;
    if (!apiBase) {
      dispatch(setError("REACT_APP_API_URL no está configurada."));
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${apiBase}/regions`);
        dispatch(setRegions(await r.json()));
      } catch (e) {
        dispatch(setError(`Cargando regiones: ${e.message}`));
      }
    })();
  }, [apiBase]);

  // 1c) index estático de comunas
  useEffect(() => {
    let cancelled = false;
    fetch(COMMUNES_INDEX_URL)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled && data) dispatch(setCommunesIndex(data)); })
      .catch(() => {/* silencioso */});
    return () => { cancelled = true; };
  }, [dispatch]);

  // 2) jams: catálogo (del dateStart) + features concatenadas del rango.
  //    No se cargan datos hasta que haya una comuna seleccionada.
  useEffect(() => {
    if (!apiBase || !selectedRegion || !selectedCommune || !dateStart || !dateEnd || !showJams) {
      dispatch(setGeojson(null));
      return;
    }
    const ctrl = new AbortController();
    dispatch(setCatalogLoading(true));
    dispatch(setGeojsonLoading(true));
    (async () => {
      try {
        const fc = await fetchRangeFeatures({
          apiBase, region: selectedRegion, layer: "jams",
          dateStart, dateEnd, timeStart, timeEnd,
          signal: ctrl.signal,
        });
        if (fc._mainCatalog) {
          dispatch(setCatalog(fc._mainCatalog));
          dispatch(setError(null));
        } else {
          dispatch(setCatalog(null));
          dispatch(setError(`Sin catálogo para ${selectedRegion} el ${dateStart}.`));
        }
        delete fc._mainCatalog;

        // Sólo las features de la comuna pedida: si el catálogo cae al
        // fallback por-región trae toda la región, y sin este recorte cada
        // comuna duplicaría el mismo set en el acumulador.
        const own = (fc.features || []).filter(
          (f) => f.properties?.city === selectedCommune,
        );
        const acc = jamsAccum.current;
        if (acc.key !== accumKey) { acc.key = accumKey; acc.byCommune = new Map(); }
        if (compareEnabled) acc.byCommune = new Map([[selectedCommune, own]]);
        else acc.byCommune.set(selectedCommune, own);
        let merged = [];
        for (const arr of acc.byCommune.values()) merged = merged.concat(arr);
        dispatch(setGeojson({ type: "FeatureCollection", features: merged }));
      } catch (e) {
        if (e.name !== "AbortError") dispatch(setError(`Jams: ${e.message}`));
      } finally {
        dispatch(setCatalogLoading(false));
        dispatch(setGeojsonLoading(false));
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, selectedRegion, dateStart, dateEnd, timeStart, timeEnd, selectedCommune, showJams, compareEnabled, accumKey]);

  // 3) alerts: idem (también requiere comuna seleccionada).
  useEffect(() => {
    if (!apiBase || !selectedRegion || !selectedCommune || !dateStart || !dateEnd || !showAlerts) {
      dispatch(setAlertsGeojson(null));
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const fc = await fetchRangeFeatures({
          apiBase, region: selectedRegion, layer: "alerts",
          dateStart, dateEnd, timeStart, timeEnd,
          signal: ctrl.signal,
        });
        delete fc._mainCatalog;

        const own = (fc.features || []).filter(
          (f) => f.properties?.city === selectedCommune,
        );
        const acc = alertsAccum.current;
        if (acc.key !== accumKey) { acc.key = accumKey; acc.byCommune = new Map(); }
        if (compareEnabled) acc.byCommune = new Map([[selectedCommune, own]]);
        else acc.byCommune.set(selectedCommune, own);
        let merged = [];
        for (const arr of acc.byCommune.values()) merged = merged.concat(arr);
        dispatch(setAlertsGeojson({ type: "FeatureCollection", features: merged }));
      } catch (e) {
        if (e.name !== "AbortError") dispatch(setError(`Alerts: ${e.message}`));
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, selectedRegion, dateStart, dateEnd, timeStart, timeEnd, selectedCommune, showAlerts, compareEnabled, accumKey]);

  // 4) comparativa: features del período B
  useEffect(() => {
    if (!compareEnabled || !compareDateStart || !compareDateEnd || !apiBase || !selectedRegion || !selectedCommune) {
      dispatch(setCompareCatalog(null));
      dispatch(setCompareGeojson(null));
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const fc = await fetchRangeFeatures({
          apiBase, region: selectedRegion, layer: "jams",
          dateStart: compareDateStart, dateEnd: compareDateEnd,
          timeStart: compareTimeStart, timeEnd: compareTimeEnd,
          signal: ctrl.signal,
        });
        if (fc._mainCatalog) dispatch(setCompareCatalog(fc._mainCatalog));
        delete fc._mainCatalog;
        dispatch(setCompareGeojson(fc));
      } catch (e) {/* ignore abort */}
    })();
    return () => ctrl.abort();
  }, [compareEnabled, compareDateStart, compareDateEnd, compareTimeStart, compareTimeEnd, selectedCommune, apiBase, selectedRegion]);

  // 5) comparativa: alertas del período B (para comparar gravedad/accidentes).
  //    Sólo si la capa de alertas está activa, igual que el rango A.
  useEffect(() => {
    if (!compareEnabled || !showAlerts || !compareDateStart || !compareDateEnd || !apiBase || !selectedRegion || !selectedCommune) {
      dispatch(setCompareAlertsGeojson(null));
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const fc = await fetchRangeFeatures({
          apiBase, region: selectedRegion, layer: "alerts",
          dateStart: compareDateStart, dateEnd: compareDateEnd,
          timeStart: compareTimeStart, timeEnd: compareTimeEnd,
          signal: ctrl.signal,
        });
        delete fc._mainCatalog;
        dispatch(setCompareAlertsGeojson(fc));
      } catch (e) {/* ignore abort */}
    })();
    return () => ctrl.abort();
  }, [compareEnabled, showAlerts, compareDateStart, compareDateEnd, compareTimeStart, compareTimeEnd, selectedCommune, apiBase, selectedRegion]);

  return null;
};

export default DataLoader;
